import React from 'react';
import type { ChatMessage } from '../context/chatTypes';
import type { PaymentRequestPayload } from '../types/paymentTypes';
import { shortenAddress } from '../utils/chatHelpers';
import AttachmentRenderer from './AttachmentRenderer';
import PaymentBubble from './PaymentBubble';
import RequestBubble from './RequestBubble';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

interface MessageBubbleProps {
  message: ChatMessage;
  showSender: boolean;
  isUnread?: boolean;
  onPayRequest?: (payload: PaymentRequestPayload) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({ message, showSender, isUnread, onPayRequest }) => {
  const timeStr = message.sentAt.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const isAttachment =
    message.contentType === 'remoteStaticAttachment' ||
    message.contentType === 'attachment' ||
    message.contentType === 'remoteAttachment';

  const isPayment = message.contentType === 'payment';
  const isRequest = message.contentType === 'paymentRequest';

  const bubbleClass = [
    'chat-message',
    message.isSentByMe ? 'chat-message--sent' : 'chat-message--received',
    isUnread ? 'chat-message--unread' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={bubbleClass}>
      {showSender && !message.isSentByMe && (
        <div className="chat-message__sender">
          {shortenAddress(message.senderAddress)}
        </div>
      )}
      <div className="chat-message__bubble">
        {isPayment ? (
          <PaymentBubble message={message} />
        ) : isRequest ? (
          <RequestBubble message={message} onPayRequest={onPayRequest} />
        ) : isAttachment ? (
          <AttachmentRenderer message={message} />
        ) : (
          <div className="chat-message__text">
            {typeof message.content === 'string'
              ? message.content
              : JSON.stringify(message.content)}
          </div>
        )}
        {!isPayment && !isRequest && (
          <span className="chat-message__time">
            {timeStr}
            {message.isSentByMe && (
              <FontAwesomeIcon icon={faCheck} className="chat-message__sent-icon" />
            )}
          </span>
        )}
      </div>
    </div>
  );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
